import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AccountMapping, UsageResponse, UserActivityResponse } from '../types';
import { ClaudeUsageApi, ValidWindow } from './ClaudeUsageApi';

export class ClaudeUsageApiClient implements ClaudeUsageApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;
  private baseUrl: string | undefined;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
  }

  private async getBaseUrl(): Promise<string> {
    if (!this.baseUrl) {
      this.baseUrl = await this.discoveryApi.getBaseUrl('claude-usage');
    }
    return this.baseUrl;
  }

  private async doRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.getBaseUrl();
    const resp = await this.fetchApi.fetch(`${base}${path}`, init);
    if (!resp.ok) {
      let message = `Request to ${path} failed with status ${resp.status} ${resp.statusText}`;
      if (resp.headers.get('Content-Type')?.includes('application/json')) {
        const json = await resp.json().catch(() => ({}));
        if (json.error) message = json.error;
        else if (json.message) message = json.message;
      }
      throw new Error(message);
    }
    return resp.json();
  }

  async getUsage(usageWindow: ValidWindow): Promise<UsageResponse> {
    return this.doRequest<UsageResponse>(`/usage?window=${usageWindow}`);
  }

  async getUserActivity(): Promise<UserActivityResponse> {
    return this.doRequest<UserActivityResponse>('/user-activity');
  }

  async getAccountMappings(): Promise<AccountMapping[]> {
    return this.doRequest<AccountMapping[]>('/category-mappings');
  }

  async addCategoryMapping(mapping: AccountMapping): Promise<AccountMapping[]> {
    return this.doRequest<AccountMapping[]>('/category-mappings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapping),
    });
  }

  async invalidateCache(): Promise<void> {
    await this.doRequest<void>('/cache/invalidate', { method: 'POST' });
  }
}
